import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, TextField, Typography } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { useConsulta } from "../hooks/useConsulta";
import { useDebounce } from "../hooks/useDebounce";
import { MINIMO_BUSQUEDA } from "../constants";
import { nombreCompleto } from "../model/solicitud";
import { buscarEstudiantes, buscarPorIdentificacion } from "../services/bibliotecaApi";

// Si lo escrito son solo digitos, sirve para preguntar por la cedula.
const esIdentificacion = (texto) => /^\d+$/.test(texto);

// Marca de la opcion que no es una persona, sino la invitacion a consultar la
// cedula en la universidad. Va dentro del desplegable porque es ahi donde mira
// quien busca: debajo del campo queda tapada por el mismo desplegable.
const RESPALDO = "__buscar_identidad__";

const esRespaldo = (opcion) => Boolean(opcion?.[RESPALDO]);

/**
 * Buscador de estudiantes para la solicitud.
 *
 * Solo deja elegir personas que ya existen: antes se escribia la
 * identificacion a mano y el nombre quedaba siendo lo mismo que se tecleara.
 *
 * Busca en dos sitios, en este orden:
 *   1. Las personas de Emma, por cedula, correo, usuario o nombre.
 *   2. Si ahi no aparece y lo escrito es una cedula, se ofrece consultarla en
 *      el servicio de identidades de la universidad, que reconoce a cualquiera
 *      con cuenta CUC, tenga o no cuenta en Emma.
 *
 * Venga de donde venga, nadie entra a la solicitud sin que lo elijan de la
 * lista: la consulta por cedula muestra a quien encontro, no lo agrega sola.
 */
const BuscadorEstudiante = ({ onAgregar, onMensaje, excluir, ayuda }) => {
  const [texto, setTexto] = useState("");
  const [desplegado, setDesplegado] = useState(false);
  const [consultandoCedula, setConsultandoCedula] = useState(false);
  const [deLaUniversidad, setDeLaUniversidad] = useState(null);
  const buscado = useDebounce(texto.trim(), 400);

  const { datos: encontrados, cargando, error } = useConsulta(
    () => buscarEstudiantes(buscado),
    [buscado],
  );

  // Lo encontrado por cedula vale para esa cedula: al cambiar la busqueda se
  // descarta, y con eso vuelve a ofrecerse consultar la nueva.
  useEffect(() => setDeLaUniversidad(null), [buscado]);

  // Quien ya esta en la solicitud no se vuelve a ofrecer, aunque este
  // desmarcado: para volver a incluirlo esta el check de la lista.
  const personas = encontrados.filter(
    ({ identificacion }) => !excluir.includes(`${identificacion}`),
  );

  const buscoSuficiente = buscado.length >= MINIMO_BUSQUEDA;
  const sinResultados = buscoSuficiente && !cargando && !error && personas.length === 0;
  const ofrecerRespaldo =
    sinResultados && esIdentificacion(buscado) && !deLaUniversidad && !consultandoCedula;

  const opciones = [
    ...personas,
    ...(deLaUniversidad ? [deLaUniversidad] : []),
    ...(ofrecerRespaldo ? [{ [RESPALDO]: true, identificacion: buscado }] : []),
  ];

  const agregar = (persona) => {
    onAgregar(persona);
    setTexto("");
    // Ya esta en la solicitud: no tiene por que seguir en el desplegable.
    setDeLaUniversidad(null);
  };

  const consultarIdentidad = async (identificacion) => {
    setConsultandoCedula(true);
    try {
      const persona = await buscarPorIdentificacion(identificacion);

      if (!persona) {
        onMensaje(`La identificacion ${identificacion} no esta registrada en la universidad.`);
        return;
      }
      if (excluir.includes(`${persona.identificacion}`)) {
        onMensaje("Esa persona ya esta en la solicitud.");
        return;
      }
      // Se muestra como una opcion mas; agregarla la decide quien busca.
      setDeLaUniversidad(persona);
      setDesplegado(true);
    } catch (fallo) {
      onMensaje("No fue posible consultar la identificacion en este momento.", "error");
    } finally {
      setConsultandoCedula(false);
    }
  };

  const elegir = (opcion) => {
    if (!opcion) return;

    if (esRespaldo(opcion)) {
      consultarIdentidad(opcion.identificacion);
      return;
    }
    agregar(opcion);
  };

  const aviso = () => {
    // Con el campo vacio manda el aviso de quien lo usa (el grupo que carga,
    // por ejemplo); apenas se escribe, lo que importa es la busqueda.
    if (ayuda && !texto) return ayuda;
    if (consultandoCedula) return "Consultando la identificacion en la universidad...";
    if (deLaUniversidad) return "Encontrado en la universidad: eligelo para agregarlo";
    if (!buscoSuficiente) {
      return `Escribe al menos ${MINIMO_BUSQUEDA} caracteres de la cedula, el correo o el nombre`;
    }
    if (cargando) return "Buscando...";
    if (error) return "No fue posible buscar en este momento";
    if (ofrecerRespaldo) return "No esta en Emma: buscalo en la universidad";
    if (personas.length === 0) {
      return "No esta en Emma. Si tienes su numero de identificacion, escribelo";
    }
    return "Elige uno de la lista para agregarlo";
  };

  const pintarOpcion = (opcion) => {
    if (esRespaldo(opcion)) {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <SearchIcon fontSize="small" color="primary" style={{ marginRight: 8 }} />
          <div>
            <Typography variant="body2" color="primary">
              {`Buscar la identificacion ${opcion.identificacion} en la universidad`}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Para estudiantes que aun no tienen cuenta en Emma
            </Typography>
          </div>
        </div>
      );
    }

    // De donde salio importa: uno esta en Emma y el otro no.
    const externo = deLaUniversidad === opcion;

    return (
      <div>
        <Typography variant="body2">{nombreCompleto(opcion)}</Typography>
        <Typography variant="caption" color="textSecondary">
          {externo
            ? `${opcion.identificacion} - encontrado en la universidad`
            : opcion.identificacion}
        </Typography>
      </div>
    );
  };

  return (
    <Autocomplete
      // Nunca queda nada seleccionado: lo elegido pasa de inmediato a la lista.
      value={null}
      options={opciones}
      inputValue={texto}
      onInputChange={(_evento, valor) => setTexto(valor)}
      onChange={(_evento, opcion) => elegir(opcion)}
      // El desplegable se controla aqui para poder volver a abrirlo cuando la
      // consulta por cedula responde: elegir una opcion lo habia cerrado.
      open={desplegado}
      onOpen={() => setDesplegado(true)}
      onClose={() => setDesplegado(false)}
      getOptionLabel={(opcion) =>
        esRespaldo(opcion) ? `${opcion.identificacion}` : nombreCompleto(opcion)
      }
      getOptionSelected={(una, otra) => una.identificacion === otra.identificacion}
      // La busqueda la hace el servidor: aqui no se vuelve a filtrar. Filtrar
      // borraria ademas la opcion de respaldo, que no es una persona.
      filterOptions={(recibidas) => recibidas}
      loading={cargando}
      noOptionsText="No esta registrado en Emma"
      renderOption={pintarOpcion}
      renderInput={(parametros) => (
        <TextField
          {...parametros}
          label="Estudiantes"
          placeholder="Busca por cedula, correo o nombre"
          fullWidth
          helperText={aviso()}
          InputProps={{
            ...parametros.InputProps,
            endAdornment: (
              <>
                {(cargando || consultandoCedula) && (
                  <CircularProgress color="inherit" size={18} />
                )}
                {parametros.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

BuscadorEstudiante.propTypes = {
  // Recibe la persona elegida, tal como la entrega el buscador de Emma.
  onAgregar: PropTypes.func.isRequired,
  // Identificaciones que ya estan en la solicitud.
  excluir: PropTypes.arrayOf(PropTypes.string).isRequired,
  onMensaje: PropTypes.func,
  // Si viene, reemplaza el aviso del buscador (p. ej. mientras carga el grupo).
  ayuda: PropTypes.string,
};

BuscadorEstudiante.defaultProps = {
  onMensaje: () => {},
  ayuda: "",
};

export default BuscadorEstudiante;
